import { Box, Typography } from "@mui/material";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { nanoid } from "nanoid";

import { colors } from "../../styles/colors";
import fontWeight from "../../styles/mui/fontWeight";
import ReferralDocument from "./ReferralDocument";

interface FileDataType {
  disabled?: boolean;
  fileName?: any;
  files: any;
  imageUrl?: string;
  patientImageProps?: any;
  setFiles: any;
  setPatientImage?: any;
  maxFileSizeInBytes?: number;
  minFileSize?: number;
  type: number;
  isVisible?: boolean;
  multipleFilesAllowed?: boolean;
  isNotification?: boolean;
}

type AllProps = FileDataType;

const FileUpload: React.FC<AllProps> = ({
  files,
  setFiles,
  maxFileSizeInBytes,
  minFileSize,
  type,
  disabled,
  isVisible = true,
  multipleFilesAllowed = true,
}: AllProps) => {
  const allowedFileTypes = [".doc", ".docx", ".pdf", ".xls", ".xlsx", ".txt"];
  const imageFileTypes = [".png", ".jpg", ".jpeg", ".jif", "jpe", ".pjp", ".pjpeg"];
  const portableFileTypes = [".pdf"];
  const faxFiletypes = {
    "image/png": imageFileTypes,
    "image/jpeg": imageFileTypes,
    "image/jpg": imageFileTypes,
    "application/pdf": portableFileTypes,
    "text/plain": [".txt"],
  };

  const portableFiletypes = {
    "application/pdf": portableFileTypes,
  };

  const acceptedFileType = (type: number) => {
    switch (type) {
      case 0:
        return faxFiletypes;
      case 2:
        return portableFiletypes;
      default:
        return {
          ...faxFiletypes,
          "application/vnd.ms-excel": allowedFileTypes,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            allowedFileTypes,
          "application/msword": allowedFileTypes,
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            allowedFileTypes,
        };
    }
  };

  const {
    getRootProps: getRootReferralFileProps,
    getInputProps: getReferralFileProps,
    isDragAccept,
    isDragReject,
    open,
    fileRejections,
  } = useDropzone({
    onDrop: useCallback(
      (acceptedFiles: any) => {
        if (acceptedFiles.length > 0) {
          setFiles((prevFiles: File[]) => [...prevFiles, ...acceptedFiles]);
        }
      },
      [files]
    ),
    accept: {
      ...acceptedFileType(type),
    },
    maxSize: maxFileSizeInBytes,
    minSize: minFileSize,
    noClick: true,
    noKeyboard: true,
    multiple: multipleFilesAllowed,
  });

  const fileRejectionItems = fileRejections.map(({ errors }) => {
    const minFileSizeForUpload = minFileSize ? minFileSize : 1;
    const maxFileSizeInMegaBytes = maxFileSizeInBytes
      ? maxFileSizeInBytes / 1000000
      : 1000000;
    return (
      <Box key={nanoid()} textAlign={"center"} mt="1rem">
        {errors.map((e: any) => {
            if (e.code === "file-invalid-type") {
            const allAllowedExtensions = [
              ...allowedFileTypes,
              ...imageFileTypes,
              ...portableFileTypes,
            ];
            const fileName = e.file?.name || "";
            const fileExt = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
            if (!allAllowedExtensions.includes(fileExt)) {
              e.message = `File is not allowed. Allowed types: ${allAllowedExtensions.join(", ")}`;
            } else {
              e.message = "File type not allowed";
            }
            } else if (e.code === "file-too-large") {
            e.message = `File is larger than ${maxFileSizeInMegaBytes} mb`;
            } else if (e.code === "file-too-small") {
            e.message = `File size should be greater than ${minFileSizeForUpload - 1} kb`;
            } else {
            e.message = e.message || "File could not be uploaded due to an unknown error";
            }
          return (
            <Typography
              variant="body1"
              color={colors.red[100]}
              key={e.code}
              fontWeight={fontWeight[3]}
              pb={"0.7rem"}
            >
              {e.message}
            </Typography>
          );
        })}
      </Box>
    );
  });

  return (
    <>
      {ReferralDocument({
        isDragAccept: isDragAccept,
        isDragReject: isDragReject,
        disabled: disabled,
        isVisible: isVisible,
        getRootReferralFileProps: getRootReferralFileProps,
        getReferralFileProps: getReferralFileProps,
        open: open,
        fileRejectionItems: fileRejectionItems,
      })}
    </>
  );
};

export default FileUpload;
